import React from 'react';
import { assertDefined } from '../../lib/util';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type {
  SingleEventType,
  Event,
  SingleEvent,
  Events,
} from '../core/TimeData';
import { Modal, ModalBody } from '../ui/index';
import type { ModalProps } from '../ui/Modal/Modal';
import SingleEventForm from './SingleEventForm';
import type { EventTimeline } from '../core/Timelines';

type SingleEventModalContentProps = {
  vesselId?: number;
  singleEvent?: SingleEvent;
  timeline?: EventTimeline;
  initialStartTime?: number;
  scheduleContext?: ScheduleDataContext;
  onPrevious: () => void;
  handleCreateEvent?: (
    type: SingleEventType,
    vesselId: number,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    duration: number,
    index: number,
    start: number,
    modalKey?: string,
  ) => void;
  handleUpdateEntry?: (
    timeline: EventTimeline,
    event: Event,
    change: Partial<Events>,
  ) => void;
};

export type SingleEventModalProps = SingleEventModalContentProps &
  Partial<ModalProps>;

const EditSingleEventModalContent = (
  props: SingleEventModalContentProps &
    Required<
      Pick<
        SingleEventModalContentProps,
        'vesselId' | 'singleEvent' | 'scheduleContext' | 'timeline'
      >
    >,
): JSX.Element => {
  const { handleUpdateEntry, timeline } = props;

  if (!handleUpdateEntry) {
    throw new Error(`handleUpdateEntry must be defined`);
  }

  if (!timeline) {
    throw new Error(`timeline must be defined`);
  }

  return (
    <SingleEventForm
      {...props}
      singleEvent={props.singleEvent}
      onCancel={props.onPrevious}
      handleUpdateEntry={(...args) => {
        handleUpdateEntry(...args);
        props.onPrevious();
      }}
    />
  );
};

const CreateSingleEventModalContent = (
  props: SingleEventModalContentProps &
    Required<
      Pick<
        SingleEventModalContentProps,
        'vesselId' | 'scheduleContext' | 'timeline'
      >
    >,
): JSX.Element => {
  const { handleCreateEvent, scheduleContext } = props;

  if (!handleCreateEvent) {
    throw new Error(`handleCreateEvent must be defined`);
  }
  assertDefined(
    scheduleContext,
    `scheduleContext should be defined for SingleEventModal`,
  );

  return (
    <SingleEventForm
      {...props}
      scheduleContext={scheduleContext}
      singleEvent={null}
      onCancel={props.onPrevious}
      handleCreateEvent={(...args) => {
        handleCreateEvent(...args);
        props.onPrevious();
      }}
    />
  );
};

const SingleEventModal = ({
  singleEvent,
  vesselId,
  scheduleContext,
  timeline,
  ...restProps
}: SingleEventModalProps): JSX.Element | null => {
  if (
    vesselId === undefined ||
    scheduleContext === undefined ||
    timeline === undefined
  ) {
    return null;
  }

  return (
    <Modal
      isOpen
      onClose={restProps.onPrevious}
      {...restProps}
      height="30em"
      width="30em"
    >
      <ModalBody p={0} overflowX="visible" overflowY="visible">
        {singleEvent !== undefined ? (
          <EditSingleEventModalContent
            vesselId={vesselId}
            singleEvent={singleEvent}
            scheduleContext={scheduleContext}
            timeline={timeline}
            {...restProps}
          />
        ) : (
          <CreateSingleEventModalContent
            vesselId={vesselId}
            scheduleContext={scheduleContext}
            timeline={timeline}
            {...restProps}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default SingleEventModal;
